/**
 * Patient diagnosis list service (Phase 6, PAT-05).
 * Routes /patientmr/{patientId}/diagnoses. DELETE is a soft delete on the server.
 */

import { apiClient } from '../api-client';
import { ApiResponse } from '../../dtos/common/api.dto';
import type { Diagnosis, DiagnosisInput } from '../../dtos/patientmr/diagnosis.dto';

type DiagnosisListPayload = Diagnosis[] | { diagnoses?: Diagnosis[]; items?: Diagnosis[] };
type DiagnosisItemPayload = Diagnosis | { diagnosis?: Diagnosis };

const unwrapItem = (data: DiagnosisItemPayload | undefined): Diagnosis | null => {
  if (!data) return null;
  if ('diagnosisId' in data) return data;
  return data.diagnosis ?? null;
};

export class DiagnosesService {
  /** Primary first, then active before resolved, then by code. */
  static async list(patientId: string): Promise<Diagnosis[]> {
    const response: ApiResponse<DiagnosisListPayload> = await apiClient.get(`/patientmr/${patientId}/diagnoses`);
    if (response.success) {
      const data = response.data;
      const rows = Array.isArray(data) ? data : data?.diagnoses || data?.items || [];
      return [...rows].sort((a, b) => {
        if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
        if (a.status !== b.status) return a.status === 'ACTIVE' ? -1 : 1;
        return a.icd10Code.localeCompare(b.icd10Code);
      });
    }
    throw new Error(response.error?.message || 'Failed to load diagnoses');
  }

  static async create(patientId: string, input: DiagnosisInput): Promise<Diagnosis | null> {
    const response: ApiResponse<DiagnosisItemPayload> = await apiClient.post(`/patientmr/${patientId}/diagnoses`, input);
    if (response.success) return unwrapItem(response.data);
    throw new Error(response.error?.message || 'Failed to add diagnosis');
  }

  static async update(patientId: string, diagnosisId: string, input: Partial<DiagnosisInput>): Promise<Diagnosis | null> {
    const response: ApiResponse<DiagnosisItemPayload> = await apiClient.put(
      `/patientmr/${patientId}/diagnoses/${diagnosisId}`,
      input
    );
    if (response.success) return unwrapItem(response.data);
    throw new Error(response.error?.message || 'Failed to update diagnosis');
  }

  static async remove(patientId: string, diagnosisId: string): Promise<void> {
    const response: ApiResponse<unknown> = await apiClient.delete(`/patientmr/${patientId}/diagnoses/${diagnosisId}`);
    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to remove diagnosis');
    }
  }
}

export default DiagnosesService;
